import {gql} from '@apollo/client';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import type {CoreObject} from 'app-core/framework/CoreObjectConstants';
import {starItems, unstarItems} from 'app-core/platform/starrable/StarDAO';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';

import {getChatNameRenderInfo} from './ChatNameUtil';
import {RenameChatModal} from './RenameChatModal';

export const CHAT_SESSION_MENU_FRAGMENT = gql`
  fragment ChatSessionMenu on ChatSessionNode {
    id
    type
    name
    initialOrigin
    modifiedAt
    star {
      id
      isStarred
    }
  }
`;

export interface ChatSessionMenuData {
  id: string;
  name: string | null;
  star?: {
    id: string;
    isStarred: boolean;
  } | null;
}

export interface ChatSessionStarUpdate {
  chatId: string;
  isStarred: boolean;
}

interface UseChatSessionMenuOptionsParams {
  chatSession: ChatSessionMenuData;
  onStarUpdated?: (update: ChatSessionStarUpdate) => Promise<void>;
}

function updateChatSessionStarInCache(chatId: string, isStarred: boolean): void {
  const client = getApolloClient();
  const cacheId = client.cache.identify({__typename: 'ChatSessionNode', id: chatId});
  if (cacheId == null) {
    return;
  }
  client.cache.modify({
    id: cacheId,
    fields: {
      star(existingStar, {readField}) {
        if (!isStarred) {
          return null;
        }
        const existingStarId = existingStar != null ? readField<string>('id', existingStar) : null;
        if (existingStarId == null) {
          // Avoid writing a headless StarNode — that can briefly corrupt the normalized
          // ChatSessionNode and flash dependent queries (e.g. the header title).
          return existingStar;
        }
        return {__typename: 'StarNode', id: existingStarId, isStarred: true};
      },
    },
  });
}

/**
 * Overflow-menu options shared by chat session row overflow menus and the redesigned header title menu.
 */
export function useChatSessionMenuOptions({
  chatSession,
  onStarUpdated,
}: UseChatSessionMenuOptionsParams): Array<OverflowMenuOption> {
  const {id, name, star} = chatSession;
  const serverIsStarred = Boolean(star?.isStarred);
  const [optimisticIsStarred, setOptimisticIsStarred] = useState<boolean | null>(null);
  const isStarred = optimisticIsStarred ?? serverIsStarred;
  const [isUpdatingStar, setIsUpdatingStar] = useState(false);
  const chatNameInfo = getChatNameRenderInfo({id, name});

  useEffect(() => {
    if (optimisticIsStarred != null && optimisticIsStarred === serverIsStarred) {
      setOptimisticIsStarred(null);
    }
  }, [optimisticIsStarred, serverIsStarred]);

  const openRenameModal = useCallback(() => {
    const key = `rename-chat-${id}`;
    LegacyModalContainer.open(
      key,
      <RenameChatModal
        chatId={id}
        currentName={chatNameInfo.text}
        onClose={() => {
          LegacyModalContainer.close(key);
        }}
      />
    );
  }, [chatNameInfo.text, id]);

  const handleClickStar = useCallback(async () => {
    if (isUpdatingStar) {
      return;
    }
    const nextIsStarred = !isStarred;
    const toggleStar = nextIsStarred ? starItems : unstarItems;
    setIsUpdatingStar(true);
    try {
      await toggleStar([{id: id as CoreObject['id']}]);
      setOptimisticIsStarred(nextIsStarred);
      if (nextIsStarred) {
        // Refresh history first so listing results hydrate a real StarNode before we patch cache.
        try {
          await onStarUpdated?.({chatId: id, isStarred: nextIsStarred});
        } catch (error) {
          if (!(error instanceof Error)) {
            throw error;
          }
          // History refresh failed, but the star mutation succeeded — still sync header state.
        }
        updateChatSessionStarInCache(id, nextIsStarred);
      } else {
        updateChatSessionStarInCache(id, nextIsStarred);
        try {
          await onStarUpdated?.({chatId: id, isStarred: nextIsStarred});
        } catch (error) {
          if (!(error instanceof Error)) {
            throw error;
          }
          // History refresh failed, but the unstar mutation succeeded — header already synced.
        }
      }
    } catch (error) {
      setIsUpdatingStar(false);
      throw error;
    }
    setIsUpdatingStar(false);
  }, [id, isStarred, isUpdatingStar, onStarUpdated]);

  return useMemo(
    () => [
      {
        icon: 'edit',
        contents: <>Rename</>,
        onSelect: openRenameModal,
      },
      {
        icon: isStarred ? 'star' : 'star-outline',
        contents: <>{isStarred ? 'Unstar' : 'Star'}</>,
        onSelect: handleClickStar,
      },
      {
        icon: 'audit-trail',
        contents: <>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</>,
        onSelect: () => {
          exportAuditLogs([id]);
        },
      },
    ],
    [handleClickStar, id, isStarred, openRenameModal]
  );
}
